// frontend/src/components/InstagramDiscoveryPage.jsx
import React, { useState } from 'react';
import contentService from '../services/contentService';
import './InstagramDiscoveryPage.css';

const DEFAULT_PROFILE_FIELDS =
  'id,username,name,biography,profile_picture_url,website,followers_count,follows_count,media_count';

const DEFAULT_MEDIA_FIELDS =
  'id,media_type,media_url,thumbnail_url,permalink,caption,timestamp,like_count,comments_count,children{media_type,media_url,thumbnail_url}';

function MediaTile({ item }) {
  const {
    id,
    media_type,
    media_url,
    thumbnail_url,
    permalink,
    caption,
    like_count,
    comments_count,
    children,
  } = item || {};

  const isVideo = media_type === 'VIDEO';
  const isCarousel = media_type === 'CAROUSEL_ALBUM';

  // Helper: escoger miniatura segura (si es video => thumbnail; si imagen => media_url)
  const pickThumb = (node) => {
    if (!node) return '';
    if (node.media_type === 'VIDEO') return node.thumbnail_url || '';
    if (node.media_type === 'IMAGE') return node.media_url || node.thumbnail_url || '';
    // por si Meta agrega más tipos
    return node.thumbnail_url || node.media_url || '';
  };

  // Para carrusel, intenta con hijos
  const childList = Array.isArray(children?.data) ? children.data : [];
  const carouselThumbs = childList.map(pickThumb).filter(Boolean).slice(0, 4);

  // Fallback general:
  // - carrusel: primera miniatura válida de hijos
  // - video: thumbnail_url
  // - imagen: media_url
  const fallbackImage =
    (isCarousel && (carouselThumbs[0] || '')) ||
    (isVideo ? (thumbnail_url || '') : (media_url || thumbnail_url || ''));

  // onError => poner un placeholder si el CDN bloquea
  const handleImgError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src =
      'data:image/svg+xml;charset=utf-8,' +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="600" height="600">
           <rect width="100%" height="100%" fill="#f3f4f6"/>
           <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
                 font-family="sans-serif" font-size="16" fill="#9ca3af">sin vista previa</text>
         </svg>`
      );
  };

  return (
    <a
      key={id}
      href={permalink}
      target="_blank"
      rel="noreferrer"
      className="igd-media-card"
      title={caption || ''}
    >
      <div className="igd-media-thumb">
        {isCarousel ? (
          <div className="igd-collage">
            {(carouselThumbs.length ? carouselThumbs : [fallbackImage, fallbackImage, fallbackImage, fallbackImage])
              .slice(0, 4)
              .map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={`${id}-${i}`}
                  className="igd-collage-img"
                  referrerPolicy="no-referrer"
                  onError={handleImgError}
                />
              ))}
            <div className="igd-badge-corner">🖼️ {childList.length || carouselThumbs.length || 1}</div>
          </div>
        ) : isVideo ? (
          <video
            className="igd-media-video"
            src={media_url}
            poster={thumbnail_url || fallbackImage || undefined}
            playsInline
            muted
            controls
            preload="metadata"
            // algunos CDNs también agradecen esto para el poster
            referrerPolicy="no-referrer"
          />
        ) : (
          <img
            className="igd-media-img"
            src={fallbackImage}
            alt={caption || id}
            referrerPolicy="no-referrer"
            onError={handleImgError}
          />
        )}
      </div>

      <div className="igd-media-meta">
        <span className="igd-meta-pill">❤️ {like_count ?? 0}</span>
        <span className="igd-meta-pill">💬 {comments_count ?? 0}</span>
        {isCarousel && <span className="igd-meta-pill">Carrusel</span>}
        {isVideo && <span className="igd-meta-pill">Video</span>}
      </div>
    </a>
  );
}

export default function InstagramDiscoveryPage() {
  const [username, setUsername] = useState('');
  const [fields, setFields] = useState(DEFAULT_PROFILE_FIELDS);

  const [includeMedia, setIncludeMedia] = useState(true);
  const [mediaLimit, setMediaLimit] = useState(8);
  const [mediaFields, setMediaFields] = useState(DEFAULT_MEDIA_FIELDS);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setPayload(null);

    const cleanUser = (username || '').trim().replace(/^@+/, '');
    if (!cleanUser) {
      setError("Parámetro 'username' es requerido");
      return;
    }

    try {
      setLoading(true);
      const params = {
        username: cleanUser,
        fields: (fields || '').trim() || DEFAULT_PROFILE_FIELDS,
      };
      if (includeMedia) {
        params.include_media = 1;
        params.media_limit = mediaLimit || 8;
        params.media_fields = (mediaFields || '').trim() || DEFAULT_MEDIA_FIELDS;
      }
      const res = await contentService.getIGBusinessDiscovery(params);
      setPayload(res.data || res);
    } catch (err) {
      const msg =
        err?.response?.data?.error?.message ||
        err?.response?.data?.detail ||
        err?.message ||
        'Error inesperado consultando la API.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const bd = payload?.business_discovery;

  return (
    <div className="igd-page">
      <h1 className="igd-title">🔎 IG Business Discovery</h1>
      <p className="igd-subtitle">
        Busca un perfil de Instagram (Business/Creator) por <b>username</b> y tráelo para crear un influencer.
      </p>

      <form className="igd-form" onSubmit={handleSubmit}>
        <input
          type="text"
          className="igd-input"
          placeholder="ingresa un @username (ej: cristiano)"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
        />
        <button className="igd-btn" type="submit" disabled={loading}>
          {loading ? 'Buscando…' : 'Buscar'}
        </button>
      </form>

      <details className="igd-adv" open>
        <summary>Campos (opcional)</summary>

        <label className="igd-label">Fields de perfil</label>
        <input
          type="text"
          className="igd-input"
          value={fields}
          onChange={(e) => setFields(e.target.value)}
        />

        <div className="igd-row">
          <label className="igd-check">
            <input
              type="checkbox"
              checked={includeMedia}
              onChange={(e) => setIncludeMedia(e.target.checked)}
            />
            Incluir últimos posts
          </label>

          <div className="igd-inline">
            <span> Límite de posts </span>
            <input
              type="number"
              min={1}
              max={25}
              className="igd-input igd-input--sm"
              value={mediaLimit}
              onChange={(e) => setMediaLimit(parseInt(e.target.value || '8', 10))}
              disabled={!includeMedia}
            />
          </div>
        </div>

        <label className="igd-label">Fields de media</label>
        <input
          type="text"
          className="igd-input"
          value={mediaFields}
          onChange={(e) => setMediaFields(e.target.value)}
          disabled={!includeMedia}
        />
      </details>

      {error && <div className="igd-alert igd-alert--error">{error}</div>}

      {bd && (
        <section className="igd-result">
          <div className="igd-card">
            <div className="igd-card-header">
              <img className="igd-avatar" src={bd.profile_picture_url} alt={bd.username} />
              <div className="igd-head-text">
                <div className="igd-name-row">
                  <h2 className="igd-name">{bd.name || bd.username}</h2>
                  <span className="igd-username">@{bd.username}</span>
                </div>
                {bd.biography && <p className="igd-bio">{bd.biography}</p>}
                {bd.website && (
                  <a className="igd-link" href={bd.website} target="_blank" rel="noreferrer">
                    {bd.website}
                  </a>
                )}
              </div>
            </div>

            <div className="igd-stats">
              <div className="igd-stat">
                <div className="igd-stat-value">
                  {bd.followers_count?.toLocaleString('es-CL') ?? 0}
                </div>
                <div className="igd-stat-label">SEGUIDORES</div>
              </div>
              <div className="igd-stat">
                <div className="igd-stat-value">
                  {bd.follows_count?.toLocaleString('es-CL') ?? 0}
                </div>
                <div className="igd-stat-label">SEGUIDOS</div>
              </div>
              <div className="igd-stat">
                <div className="igd-stat-value">
                  {bd.media_count?.toLocaleString('es-CL') ?? 0}
                </div>
                <div className="igd-stat-label">PUBLICACIONES</div>
              </div>
            </div>

            <div className="igd-ids">
              <span>IG User ID (business_discovery.id): <b>{bd.id}</b></span>
              {payload?.id && <span>Nodo ID de respuesta: <b>{payload.id}</b></span>}
            </div>

            {Array.isArray(bd.media?.data) && bd.media.data.length > 0 && (
              <>
                <h3 className="igd-h3">Últimos posts</h3>
                <div className="igd-media-grid">
                  {bd.media.data.map((m) => (
                    <MediaTile key={m.id} item={m} />
                  ))}
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
